import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { APIService } from './api.service';
import { ReportConfig } from '../classes/reports/report-config';
import { ReportBlock } from 'app/classes/reports/report-block';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Report } from 'app/classes/reports/report';

@Injectable({ providedIn: 'root' })
export class ReportService {
  private _block: BehaviorSubject<any> = new BehaviorSubject(null);
  public block: Observable<any> = this._block.asObservable();

  static readonly API_URL = 'https://lycsk61ty9.execute-api.eu-west-2.amazonaws.com/prod/';

  constructor(private apiService: APIService, private http: HttpClient,) { }

  async getReport(reportId: number) {
    const params = new HttpParams().set('a', 'get-one').set('r', reportId);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .get<IReportGet>(ReportService.API_URL + 'reports', options))
      .then((response) => {
        const body = response;
        const report = this.importReport(body);

        return report;
      });
  }


  /**
   * Import a received array into a RulePackage object
   * @param receivedPackage an array received by the api
   */
  importReport(record: IReportGet) {
    const { master, blocks, collections } = record;

    let report = new Report();

    report.title = master.title;
    report.id = master.id;
    report.icon = master.icon;
    report.forAssetType = master.forAssetType;
    report.forAsset = master.forAsset;
    report.forGatewayType = master.forGatewayType;
    report.forGateway = master.forGateway;
    report.dateSpan = master.dateSpan;
    report.collections = collections;

    // Rule conditions
    for (let idx = 0; idx < blocks.length; idx++) {
      let block = blocks[idx];
      const reportBlock = new ReportBlock(block.id, block.title, block.fragment_id, block.pagebreak, block.options);
      report.blocks.push(reportBlock);
    }

    return report;
  }

  async getBlockDataV2(reportId: number, block: ReportBlock, config: ReportConfig, collectionId: number) {
    if (!config) {
      throw new Error('NO_CONFIG');
    }
    const body = {
      asset: (config.forAsset ? config.forAsset.id : null),
      gateway: (config.forGateways ? config.forGateways[0].id : null),
      forIdentifier: (config.forIdentifier ? config.forIdentifier : null),
      startAt: config.startAt,
      startOfMonth: config.startOfMonth,
      endAt: config.endAt,
      collection: (config.forCollections ? config.forCollections[0] : null)
    };

    const params = new HttpParams().set('a', 'get-block').set('r', reportId).set('b', block.id).set('c', collectionId);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .post<any[]>(ReportService.API_URL + 'blocks/', body, options))
      .then((results) => {

        let master;
        if (results[0].length) {
          master = results[0][0];
          results[0].splice(0, 1);
        } else {
          master = results[0];
          results.splice(0, 1);
        }

        master.gateway = config.forGateways[0];

        return { reportId, block, data: results, master };
      });
  }

  getBlockData(reportId: number, block: ReportBlock, config: ReportConfig) {
    this.apiService.getReportBlockData(reportId, block.id, config)
      .then(results => {
        let master;
        if (results[0].length) {
          // MultiStatement response, inject fragment data on first array
          master = results[0][0];
          results[0].splice(0, 1);
        } else {
          master = results[0];
          results.splice(0, 1);
        }

        master.gateway = config.forGateways[0];

        this._block.next({ reportId, block, data: results, master });
        this._block.next(null); // Don't leave a block around
      });
  }

  /**
   * Colour for a day
   * @param day The day of the week as a three character string
   * @returns string
   */
  colourForDay(day: string): string {
    switch (day.toLowerCase()) {
      case 'mon':
        return '#02B801';
      case 'tue':
        return '#565656';
      case 'wed':
        return '#167ac6';
      case 'thu':
        return '#8B20BB';
      case 'fri':
        return '#fdba1e';
      case 'sat':
        return '#cc181e';
      case 'sun':
        return '#4182ef';
    }

    return '#000';
  }
}


export interface IReportGet {
  blocks: any[];
  master: any;
  collections: any[];
}
